import { t } from '@/utils/i18n'
import { useState, useRef, useMemo } from 'react'
import { Dropdown, Toast } from 'antd-mobile'
import { utils, image, apis } from '@/utils'
import styles from './index.module.less'
import Icon from '@/components/Icon/Icon'
import FileUpload from '@/components/FileUpload'
import IconClose from '@/assets/img/icon/close.svg'
import sortIcon from '@/assets/img/icon/sort-double.svg'
import sortDownIcon from '@/assets/img/icon/sort-double-down.svg'
import sortUpIcon from '@/assets/img/icon/sort-double-up.svg'

const { debounce, to } = utils
const { thirdApi } = apis
const { imageCompress, imageToBase64 } = image

interface SearchBarProps {
  customizeStyle?: any
  placeholder?: string
  text?: string
  onSearch?: (searchText: string) => void
  onFilter?: (filter: any) => void
  onImageChoose?: (imageId: string) => void
  className?: string
}

interface Filter {
  sortKey: string
  sortDirection: string
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = t('搜索'),
  text = '',
  onSearch = () => {},
  onFilter = () => {},
  onImageChoose = () => {},
  className,
  customizeStyle,
}) => {
  const dropdownRef = useRef<any>(null)
  const [searchText, setSearchText] = useState(text)
  const [imageId, setImageId] = useState('')
  const [filter, setFilter] = useState<Filter>({
    sortKey: 'monthSold',
    sortDirection: 'desc',
  })
  const style = {
    backgroundColor: '#f0f0f0',
    ...customizeStyle,
  }

  const handleInput = debounce((e: any) => {
    const { value } = e.target
    setImageId('')
    onImageChoose('')
    onSearch(value)
  }, 300)

  const handleResetSearch = (e: any) => {
    e.stopPropagation()
    setSearchText('')
    onSearch('')
    setImageId('')
    onImageChoose('')
  }

  // 选择了图片后
  async function imageChosen(files: File[] = []) {
    const file = files[0]
    const compressFile = await imageCompress(file, {
      quality: 0.7,
      maxSize: 100,
    })
    let base64 = await imageToBase64(compressFile)
    base64 = base64.replace(/data:.+?base64,/, '')
    const imageId = await onUploadFile(base64)
    setImageId(imageId)
    onImageChoose(imageId)
    setSearchText('')
    onSearch('')
  }

  // 上传图片，获取图片id
  async function onUploadFile(base64: string) {
    const [err, res] = await to(
      thirdApi.thirdImageUpload({ imageBase64: base64 }),
    )

    if (err || !res) {
      Toast.show(t('上传图片失败'))
      return
    }

    return res.data
  }

  function onSortClick(key: string) {
    let newFilter: Filter = {} as Filter
    if (filter.sortKey === key) {
      newFilter = {
        ...filter,
        sortDirection: filter.sortDirection === 'asc' ? 'desc' : 'asc',
      }
    } else {
      newFilter = {
        sortKey: key,
        sortDirection: 'asc',
      }
    }
    setFilter(newFilter)
    onSearch(searchText)
    onFilter(newFilter)
    dropdownRef.current?.close()
  }

  const inputSortIcon = useMemo(() => {
    if (filter.sortDirection === 'asc') {
      return sortUpIcon
    } else if (filter.sortDirection === 'desc') {
      return sortDownIcon
    }
    return sortIcon
  }, [filter.sortDirection])

  return (
    <div className={`${styles['search-bar']} ${className}`}>
      <div className={styles['bd']} style={style}>
        <div className={styles['title-search']}>
          {(searchText || imageId) && (
            <Dropdown ref={dropdownRef}>
              <Dropdown.Item
                key="sorter"
                title={
                  <img
                    className={styles['sort-icon']}
                    src={inputSortIcon}
                    alt=""
                  />
                }
              >
                <div
                  className={`${styles['sort-item']} ${
                    filter.sortKey === 'price' ? styles['sort-item-acitve'] : ''
                  }`}
                  onClick={() => onSortClick('price')}
                >
                  <span>{t('批发价')}</span>
                  {filter.sortKey === 'price' && (
                    <img
                      src={
                        filter.sortDirection === 'asc'
                          ? sortUpIcon
                          : sortDownIcon
                      }
                      alt=""
                    />
                  )}
                </div>
                <div
                  className={`${styles['sort-item']} ${
                    filter.sortKey === 'rePurchaseRate'
                      ? styles['sort-item-acitve']
                      : ''
                  }`}
                  onClick={() => onSortClick('rePurchaseRate')}
                >
                  <span>{t('复购率')}</span>
                  {filter.sortKey === 'rePurchaseRate' && (
                    <img
                      src={
                        filter.sortDirection === 'asc'
                          ? sortUpIcon
                          : sortDownIcon
                      }
                      alt=""
                    />
                  )}
                </div>
                <div
                  className={`${styles['sort-item']} ${
                    filter.sortKey === 'monthSold'
                      ? styles['sort-item-acitve']
                      : ''
                  }`}
                  onClick={() => onSortClick('monthSold')}
                >
                  <span>{t('月销量')}</span>
                  {filter.sortKey === 'monthSold' && (
                    <img
                      src={
                        filter.sortDirection === 'asc'
                          ? sortUpIcon
                          : sortDownIcon
                      }
                      alt=""
                    />
                  )}
                </div>
              </Dropdown.Item>
            </Dropdown>
          )}

          <Icon name="search" color="#7f7f7f" size="40" />

          <input
            className={styles['title-search-input']}
            type="text"
            placeholder={placeholder}
            value={searchText}
            onInput={handleInput}
            onChange={(e: any) => {
              setSearchText(e.target.value)
            }}
          />

          {(searchText || imageId) && (
            <img
              className={styles['title-search-close']}
              src={IconClose}
              onClick={handleResetSearch}
            />
          )}

          <FileUpload
            onChange={imageChosen}
            className={styles['title-camera-wrap']}
          >
            <Icon name="camera" color="#000000" size="48" />
          </FileUpload>
        </div>
      </div>
    </div>
  )
}

export default SearchBar
